import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DollarSign, Loader2, Wallet } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Take-payment dialog. Given an order, records a partial or full payment,
 * updates the order's paid_amount, and notifies the caller.
 */
export default function PaymentDialog({ order, open, onOpenChange, onSaved }) {
  const [amount, setAmount] = useState(0);
  const [method, setMethod] = useState("havale");
  const [receiptNo, setReceiptNo] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const remaining = order ? Math.max(0, (order.total_price || 0) - (order.paid_amount || 0)) : 0;

  useEffect(() => {
    if (open && order) {
      setAmount(remaining);
      setMethod("havale");
      setReceiptNo("");
      setNotes("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order?.id]);

  const submit = async () => {
    const val = Number(amount);
    if (!val || val <= 0) return toast.error("Tutar 0'dan büyük olmalı");
    if (val > remaining + 0.01) return toast.error(`Kalan bakiye ₺${fmt(remaining)}`);
    setSaving(true);
    try {
      await api.post(`/finance/payments`, {
        order_id: order.id,
        amount: val,
        method,
        receipt_no: receiptNo,
        notes,
      });
      toast.success(`₺${fmt(val)} ödeme kaydedildi`);
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-emerald-500" />
            Tahsilat Ekle · {order.order_no}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2 text-sm">
            <MetricBlock label="Toplam" value={`₺${fmt(order.total_price)}`} />
            <MetricBlock label="Ödenen" value={`₺${fmt(order.paid_amount || 0)}`} color="emerald" />
            <MetricBlock label="Kalan" value={`₺${fmt(remaining)}`} color={remaining > 0 ? "red" : "emerald"} />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Tahsilat Tutarı (₺)</Label>
            <Input type="number" step="0.01" value={amount} data-testid="payment-amount-input"
              onChange={(e) => setAmount(e.target.value)}
              className="rounded-sm font-mono text-lg h-11" />
            <div className="flex gap-1.5 flex-wrap">
              <button type="button" onClick={() => setAmount(remaining)} data-testid="payment-full-btn"
                className="text-[11px] px-2 py-1 rounded-sm border border-border bg-muted hover:bg-muted/60">
                Kalan Tümü (₺{fmt(remaining)})
              </button>
              <button type="button" onClick={() => setAmount(remaining / 2)}
                className="text-[11px] px-2 py-1 rounded-sm border border-border bg-muted hover:bg-muted/60">
                %50
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Yöntem</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="rounded-sm" data-testid="payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nakit">Nakit</SelectItem>
                  <SelectItem value="havale">Havale / EFT</SelectItem>
                  <SelectItem value="kart">Kredi Kartı</SelectItem>
                  <SelectItem value="cek">Çek</SelectItem>
                  <SelectItem value="senet">Senet</SelectItem>
                  <SelectItem value="diger">Diğer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Makbuz / Dekont No</Label>
              <Input value={receiptNo} onChange={(e) => setReceiptNo(e.target.value)}
                data-testid="payment-receipt-input"
                className="rounded-sm font-mono" placeholder="opsiyonel" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Notlar</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              className="rounded-sm resize-none" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">İptal</Button>
          <Button onClick={submit} disabled={saving} data-testid="payment-save-btn"
            className="bg-emerald-600 hover:bg-emerald-500 rounded-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><DollarSign className="w-4 h-4 mr-1" /> Kaydet</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MetricBlock({ label, value, color }) {
  const cls = {
    emerald: "text-emerald-600 dark:text-emerald-400",
    red: "text-red-600 dark:text-red-400",
  }[color] || "text-foreground";
  return (
    <div className="p-2 border border-border rounded-sm">
      <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">{label}</div>
      <div className={`text-sm font-mono font-bold mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}
